import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { portal as portalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Markdown from '../components/Markdown';
import SegmentMiniMap from '../components/SegmentMiniMap';
import { formatDate, formatTime } from '../utils/portal';
import { segmentName } from '../utils/segments';

// Matches the title the backend gives the appended closing topic.
const CLOSER_TITLE = 'Schedule the next meeting';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Not yet discussed' },
  { value: 'discussed', label: 'Discussed' },
  { value: 'deferred', label: 'Defer to next meeting' },
  { value: 'skipped', label: 'Skip (leave out of minutes)' },
];

/**
 * Walks the meeting one agenda topic at a time: the topic's text, any trails it
 * mentions, and a notes box. Notes save on their own — nobody should have to
 * remember to press save while running a meeting.
 */
export default function RunMeetingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isChair } = useAuth();
  const canManage = isAdmin || isChair;

  const [meeting, setMeeting] = useState(null);
  const [topics, setTopics] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [notes, setNotes] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [segments, setSegments] = useState([]);
  const [addingSegment, setAddingSegment] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [scheduled, setScheduled] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  const saveTimer = useRef(null);
  const topic = topics[index] || null;

  const load = useCallback(async () => {
    try {
      const data = await portalApi.getTopics(id);
      setMeeting(data.meeting);
      setTopics(data.topics);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Notes autosave on a short delay; closing the tab inside that window would
  // drop the last keystrokes, so warn instead of losing them silently.
  useEffect(() => {
    const warn = (e) => {
      if (!saveTimer.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  useEffect(() => {
    if (canManage) portalApi.listSegments().then(setSegments).catch(() => {});
  }, [canManage]);

  useEffect(() => {
    portalApi.getOverview()
      .then((o) => setNextDate(o.next_scheduled_date))
      .catch(() => {});
  }, []);

  // Pull the current topic's notes into the box whenever the topic changes.
  useEffect(() => {
    if (topic) setNotes(topic.live_notes || '');
    setSaveState('idle');
  }, [topic?.id]); // eslint-disable-line

  const persist = useCallback(async (patch, topicId) => {
    setSaveState('saving');
    try {
      const updated = await portalApi.updateTopic(topicId, patch);
      setTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setSaveState('saved');
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setSaveState('idle');
    }
  }, []);

  // Debounced autosave — a meeting is no place to hunt for a save button.
  const onNotesChange = (value) => {
    setNotes(value);
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const topicId = topic.id;
    saveTimer.current = setTimeout(() => persist({ live_notes: value }, topicId), 700);
  };

  // Don't lose an in-flight edit when moving between topics.
  const flushThen = async (fn) => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
      await persist({ live_notes: notes }, topic.id);
    }
    fn();
  };

  const go = (next) => flushThen(() => setIndex(Math.max(0, Math.min(topics.length - 1, next))));

  const setStatus = (status) => persist({ status }, topic.id);

  const scheduleNext = async () => {
    setScheduling(true);
    try {
      const created = await portalApi.createMeeting({ meeting_date: nextDate });
      setScheduled(created);
      setMessage(`${created.title} scheduled for ${formatDate(created.meeting_date)}.`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setScheduling(false);
    }
  };

  const attachSegment = async (segmentId) => {
    if (!segmentId) return;
    try {
      const updated = await portalApi.attachTopicSegment(topic.id, segmentId);
      setTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setAddingSegment('');
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const detachTrail = async (trailSegments) => {
    try {
      let updated = null;
      for (const s of trailSegments) {
        updated = await portalApi.detachTopicSegment(topic.id, s.segment_id);
      }
      if (updated) setTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  if (loading) return <div className="loading">Loading meeting…</div>;

  if (!meeting) {
    return (
      <div className="portal-page">
        <div className="alert alert-error">{message || 'Meeting not found'}</div>
        <Link to="/volunteer/meetings">← All meetings</Link>
      </div>
    );
  }

  if (topics.length === 0) {
    return (
      <div className="portal-page">
        <div className="portal-header">
          <div>
            <p className="portal-subtitle"><Link to={`/volunteer/meetings/${id}`}>← Back to the meeting</Link></p>
            <h1>{meeting.title}</h1>
          </div>
        </div>
        <div className="portal-empty">
          <p>This meeting has no topics yet. They are generated from the agenda, one per section.</p>
          {canManage && (
            <button
              className="btn-sm btn-primary"
              onClick={async () => {
                try {
                  const r = await portalApi.generateTopics(id);
                  setTopics(r.topics);
                  setMessage(`${r.created} topics ready · ${r.segments_auto_linked} trail segments linked automatically.`);
                } catch (err) { setMessage(`Error: ${err.message}`); }
              }}
            >
              Generate topics from the agenda
            </button>
          )}
        </div>
        {message && <div className="alert alert-error">{message}</div>}
      </div>
    );
  }

  // Group this topic's segments by trail so a 12-segment trail shows one map.
  const byTrail = {};
  (topic.segments || []).forEach((s) => {
    (byTrail[s.trail_name] = byTrail[s.trail_name] || []).push(s);
  });

  const done = topics.filter((t) => t.status !== 'pending').length;

  return (
    <div className="run-page">
      <div className="run-bar">
        <Link className="run-exit" to={`/volunteer/meetings/${id}`}>✕ Exit</Link>
        <div className="run-bar-title">
          <strong>{meeting.title}</strong>
          <span>{formatDate(meeting.meeting_date, { month: 'short', day: 'numeric' })}
            {meeting.start_time && ` · ${formatTime(meeting.start_time)}`}</span>
        </div>
        <div className="run-progress">
          <span>{done} of {topics.length} covered</span>
          <div className="run-progress-bar"><div style={{ width: `${(done / topics.length) * 100}%` }} /></div>
        </div>
      </div>

      <div className="run-rail">
        {topics.map((t, i) => (
          <button
            key={t.id}
            className={`run-pip status-${t.status} ${i === index ? 'current' : ''}`}
            title={`${i + 1}. ${t.title}`}
            onClick={() => go(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="run-body">
        <div className="run-main">
          <div className="run-topic-head">
            <span className="run-topic-counter">Topic {index + 1} of {topics.length}</span>
            <h1>{topic.title}</h1>
          </div>

          {topic.agenda_md
            ? <Markdown className="run-agenda">{topic.agenda_md}</Markdown>
            : <p className="portal-muted">No agenda detail for this topic.</p>}

          {Object.keys(byTrail).length > 0 && (
            <div className="run-maps">
              {Object.entries(byTrail).map(([trailName, segs]) => (
                <SegmentMiniMap
                  key={trailName}
                  trailName={trailName}
                  segments={segs}
                  onRemove={canManage ? () => detachTrail(segs) : null}
                />
              ))}
            </div>
          )}

          {topic.title === CLOSER_TITLE && (
            <div className="run-schedule">
              {scheduled ? (
                <>
                  <p className="run-schedule-done">✅ <strong>{scheduled.title}</strong> is on the calendar for {formatDate(scheduled.meeting_date)}.</p>
                  <Link className="btn-sm btn-primary" to={`/volunteer/meetings/${scheduled.id}`}>
                    Open it to write the agenda →
                  </Link>
                </>
              ) : (
                <>
                  <p className="run-schedule-lead">
                    Next second Tuesday: <strong>{nextDate ? formatDate(nextDate) : '…'}</strong>
                  </p>
                  {canManage ? (
                    <>
                      <label className="run-schedule-date">
                        Date
                        <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
                      </label>
                      <button className="btn-sm btn-primary" onClick={scheduleNext} disabled={scheduling || !nextDate}>
                        {scheduling ? 'Scheduling…' : 'Schedule it'}
                      </button>
                      <p className="portal-muted">
                        Creates the meeting. You can send the calendar invite from its page once the agenda is ready.
                      </p>
                    </>
                  ) : (
                    <p className="portal-muted">A committee chair can schedule it from here.</p>
                  )}
                  <Link className="run-schedule-alt" to="/volunteer/meetings">Or pick another date →</Link>
                </>
              )}
            </div>
          )}

          {canManage && segments.length > 0 && (
            <div className="run-add-segment">
              <label>
                Add a trail segment to this topic
                <select value={addingSegment} onChange={(e) => attachSegment(e.target.value)}>
                  <option value="">Choose a segment…</option>
                  {segments.map((s) => (
                    <option key={s.id} value={s.id}>
                      {segmentName(s)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        <div className="run-notes">
          <div className="run-notes-head">
            <h2>Notes</h2>
            <span className={`run-save run-save-${saveState}`}>
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
            </span>
          </div>
          <textarea
            className="run-notes-input"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Type as the discussion happens. Rough is fine — you'll tidy these up afterwards."
          />

          <div className="run-status">
            {STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                className={`run-status-btn ${topic.status === o.value ? 'active' : ''} status-${o.value}`}
                onClick={() => setStatus(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="run-nav">
        <button className="btn-sm btn-secondary" disabled={index === 0} onClick={() => go(index - 1)}>
          ← Previous
        </button>
        <span className="run-nav-label">{topic.title}</span>
        {index < topics.length - 1 ? (
          <button className="btn-sm btn-primary" onClick={() => go(index + 1)}>Next →</button>
        ) : (
          <button className="btn-sm btn-primary" onClick={() => flushThen(() => navigate(`/volunteer/meetings/${id}/review`))}>
            Finish & review →
          </button>
        )}
      </div>
    </div>
  );
}
